import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPlans } from '../redux/slices/planSlice';
import { createCheckoutSession } from '../redux/slices/subscriptionSlice';
import { loadStripe } from '@stripe/stripe-js';
import { Check, Zap } from 'lucide-react';
import toast from 'react-hot-toast';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const Subscriptions = () => {
  const dispatch = useDispatch();
  const { plans, isLoading } = useSelector((state) => state.plan);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getPlans());
  }, [dispatch]);

  const handleSubscribe = async (planId) => {
    try {
      const result = await dispatch(createCheckoutSession(planId)).unwrap();
      
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      toast.error(error || 'Failed to create checkout session');
    }
  };

  const isCurrentPlan = (planId) => {
    return user?.subscription?.plan_id?._id === planId && user?.subscription?.status === 'active';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900">Choose Your Plan</h1>
        <p className="text-gray-600 mt-4 text-lg">
          Select the perfect plan for your invoicing needs
        </p>
      </div>

      {/* Current Plan Info */}
      {user?.subscription?.status === 'active' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-3xl mx-auto">
          <div className="flex items-center">
            <Check className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800">
              You're currently on the <strong>{user.subscription.plan_id?.name}</strong> plan
            </p>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {plans.map((plan) => (
          <div
            key={plan._id}
            className={`card relative ${
              isCurrentPlan(plan._id) ? 'ring-2 ring-primary-500' : ''
            }`}
          >
            {plan.name === 'Pro' && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  <Zap className="w-3 h-3" />
                  <span>Popular</span>
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">
                  ${plan.price}
                </span>
                <span className="text-gray-600">/{plan.interval}</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  {plan.features.invoicesPerMonth === -1 
                    ? 'Unlimited invoices' 
                    : `${plan.features.invoicesPerMonth} invoices/month`}
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">PDF export</span>
              </li>
              {plan.features.customBranding && (
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Custom branding</span>
                </li>
              )}
              {plan.features.clientManagement && (
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Client management</span>
                </li>
              )}
            </ul>

            {plan.price === 0 ? (
              <button
                disabled
                className="w-full btn btn-secondary"
              >
                {isCurrentPlan(plan._id) ? 'Current Plan' : 'Free Plan'}
              </button>
            ) : isCurrentPlan(plan._id) ? (
              <button disabled className="w-full btn btn-secondary">
                Current Plan
              </button>
            ) : (
              <button
                onClick={() => handleSubscribe(plan._id)}
                className="w-full btn btn-primary"
              >
                Subscribe
              </button>
            )}
          </div>
        ))}
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto pt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-2">What happens if I exceed my invoice limit?</h3>
            <p className="text-gray-600">
              You&apos;ll see how many invoices you have left before you hit the cap. At the limit, upgrade to keep creating invoices.
            </p>
          </div>
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-2">Can I change plans?</h3>
            <p className="text-gray-600">
              You can subscribe to a paid plan from this page. Cancellation takes effect at the end of the billing period.
            </p>
          </div>
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-2">Is my payment information secure?</h3>
            <p className="text-gray-600">
              Yes, all payments are processed securely through Stripe. We never store your payment information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;

